import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from './CustomerLayout';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../../components/ui/Button';
import supabase from '../../supabase/supabase';

export default function CustomerApp() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [recentBookings, setRecentBookings] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles_1740423033529')
            .select('full_name')
            .eq('id', user.id)
            .single();
          setUserName(profile?.full_name || user.email.split('@')[0]);

          // Fetch recent bookings for quick status
          const { data: bookings } = await supabase
            .from('bookings_1740423033529')
            .select(`*, barber:barber_id(full_name, avatar_url), service:service_id(name)`)
            .eq('customer_id', user.id)
            .order('created_at', { ascending: false })
            .limit(3);
          setRecentBookings(bookings || []);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statusColor = (status) => {
    if (status === 'confirmed') return 'bg-blue-50 text-blue-600 border-blue-100';
    if (status === 'completed') return 'bg-green-50 text-green-600 border-green-100';
    if (status === 'cancelled') return 'bg-red-50 text-red-600 border-red-100';
    return 'bg-gray-50 text-gray-500 border-gray-100';
  };

  return (
    <CustomerLayout>
      <div className="p-6 space-y-8">
        {/* Greeting */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-brand-dark tracking-tight">
              Ayubowan, {userName ? userName.split(' ')[0] : 'Guest'}!
            </h1>
            <div className="flex items-center gap-2 text-xs mt-1">
              <SafeIcon name="MapPin" className="text-brand-primary" />
              <span className="text-gray-400 font-bold uppercase tracking-widest">Sri Lanka</span>
            </div>
          </div>
        </div>

        {/* Hero CTA Banner */}
        <div className="bg-brand-dark rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute right-0 top-0 w-48 h-48 bg-brand-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute right-8 bottom-0 w-32 h-32 bg-brand-accent/20 rounded-full blur-2xl translate-y-1/2" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 border border-white/10">
              <SafeIcon name="Zap" className="text-brand-accent text-xs" />
              On-Demand Grooming
            </div>
            <h2 className="text-3xl font-black mb-3 leading-tight tracking-tight">
              Professional Grooming<br />at Your Doorstep
            </h2>
            <p className="text-white/60 text-xs mb-6 font-bold uppercase tracking-widest">
              Elite island-wide barbers · Same day booking
            </p>
            <Button
              onClick={() => navigate('/app/book')}
              className="bg-white text-brand-dark hover:bg-brand-accent hover:text-white shadow-none px-8 font-black rounded-2xl">
              <SafeIcon name="Scissors" className="mr-2" />
              Book a Barber Now
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/app/book')}
            className="bg-brand-primary/5 border border-brand-primary/10 rounded-2xl p-5 flex flex-col items-center gap-3 hover:bg-brand-primary/10 active:scale-95 transition-all">
            <div className="w-12 h-12 bg-brand-primary rounded-2xl flex items-center justify-center shadow-lg shadow-brand-primary/20">
              <SafeIcon name="Plus" className="text-white text-xl" />
            </div>
            <span className="text-xs font-black text-brand-dark uppercase tracking-widest">New Booking</span>
          </button>
          <button
            onClick={() => navigate('/app/bookings')}
            className="bg-gray-50 border border-gray-100 rounded-2xl p-5 flex flex-col items-center gap-3 hover:bg-gray-100 active:scale-95 transition-all">
            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-gray-100">
              <SafeIcon name="Calendar" className="text-brand-dark text-xl" />
            </div>
            <span className="text-xs font-black text-brand-dark uppercase tracking-widest">My Bookings</span>
          </button>
        </div>

        {/* Recent Bookings */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-black text-brand-dark tracking-tight">Recent Bookings</h3>
            <button
              onClick={() => navigate('/app/bookings')}
              className="text-xs font-black text-brand-primary uppercase tracking-widest flex items-center gap-1">
              View All <SafeIcon name="ChevronRight" className="text-xs" />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 opacity-50 font-bold uppercase text-[10px] tracking-widest">
              Loading...
            </div>
          ) : recentBookings.length === 0 ? (
            <div className="bg-white rounded-[2rem] p-10 text-center border border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon name="Calendar" className="text-gray-300 text-2xl" />
              </div>
              <p className="text-sm font-bold text-gray-500 mb-2">No bookings yet</p>
              <p className="text-xs text-gray-400 mb-6">Book your first professional barber today!</p>
              <Button onClick={() => navigate('/app/book')} size="sm">
                Book Now
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentBookings.map(booking => (
                <div
                  key={booking.id}
                  className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4 cursor-pointer hover:border-brand-primary/20 transition-all active:scale-[0.99]"
                  onClick={() => navigate('/app/bookings')}>
                  <img
                    src={booking.barber?.avatar_url || `https://ui-avatars.com/api/?name=${booking.barber?.full_name || 'B'}&background=0047FF&color=fff`}
                    className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                    alt=""
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-brand-dark text-sm truncate">
                      {booking.barber?.full_name || 'Barber'}
                    </p>
                    <p className="text-xs text-gray-500 font-bold truncate">{booking.service?.name}</p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">{booking.booking_date} · {booking.booking_time}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[9px] font-black uppercase border flex-shrink-0 ${statusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}